import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HelpCircle, DollarSign, TrendingUp, MapPin, BarChart3, ExternalLink } from 'lucide-react';
import { generateSalaryPageUrl, extractLocationContext, createOccupationSlug } from '@/lib/occupationUtils';
import { cn } from '@/lib/utils';

interface SocCodeBadgeProps {
  socCode: string;
  occupation?: {
    title?: string;
    occ_title?: string;
    slug?: string;
  };
  location?: string;
  variant?: 'default' | 'secondary' | 'outline';
  showHelpIcon?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const SocCodeBadge: React.FC<SocCodeBadgeProps> = ({
  socCode,
  occupation,
  location,
  variant = 'secondary',
  showHelpIcon = true,
  className,
  size = 'md',
}) => {
  const navigate = useNavigate();
  const currentLocation = useLocation();
  
  // Determine target location
  const targetLocation = location || extractLocationContext(currentLocation.pathname) || 'united-states';
  
  // Get occupation title and slug
  const occupationTitle = occupation?.title || occupation?.occ_title || '';
  const occupationSlug = occupation?.slug || (occupationTitle ? createOccupationSlug(occupationTitle) : '');
  
  // Generate URLs for different metrics
  const salaryUrl = occupationSlug && occupationTitle
    ? generateSalaryPageUrl({ ...occupation, soc: socCode, soc_code: socCode }, targetLocation)
    : null;
  
  const trendsUrl = occupationSlug && occupationTitle
    ? `/trends/${occupationSlug}/${targetLocation}/${socCode ? `?soc=${socCode}` : ''}`
    : null;
  
  const bestStatesUrl = occupationSlug
    ? `/best-states/${occupationSlug}/${socCode ? `?soc=${socCode}` : ''}`
    : null;
  
  const rankingsUrl = socCode
    ? `/rankings/?soc=${socCode.substring(0, 2)}-`
    : '/rankings/';
  
  const socGuideUrl = `/what-is-soc-code/${socCode ? `?example=${socCode}` : ''}`;
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };
  
  const handleNavigate = (url: string) => {
    navigate(url);
  };
  
  return (
    <div className={cn('inline-flex items-center gap-1', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              sizeClasses[size],
              variant === 'default' && 'border-transparent bg-primary text-primary-foreground',
              variant === 'secondary' && 'border-transparent bg-secondary text-secondary-foreground',
              variant === 'outline' && 'text-foreground'
            )}
            aria-label={`SOC Code ${socCode} - View related metrics`}
            title={`SOC Code ${socCode}${occupationTitle ? ` - ${occupationTitle}` : ''}`}
          >
            <code className="font-mono font-semibold">{socCode}</code>
            {showHelpIcon && (
              <HelpCircle className="h-3 w-3 opacity-70" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            SOC Code: {socCode}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {salaryUrl && occupationTitle && (
            <DropdownMenuItem
              onClick={() => handleNavigate(salaryUrl)}
              className="cursor-pointer"
            >
              <DollarSign className="mr-2 h-4 w-4" />
              <span>View Salary Details</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {occupationTitle}
              </span>
            </DropdownMenuItem>
          )}
          
          {trendsUrl && occupationTitle && (
            <DropdownMenuItem
              onClick={() => handleNavigate(trendsUrl)}
              className="cursor-pointer"
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              <span>View Salary Trends</span>
              <span className="ml-auto text-xs text-muted-foreground">
                Historical data
              </span>
            </DropdownMenuItem>
          )}
          
          {bestStatesUrl && (
            <DropdownMenuItem
              onClick={() => handleNavigate(bestStatesUrl)}
              className="cursor-pointer"
            >
              <MapPin className="mr-2 h-4 w-4" />
              <span>Compare States</span>
              <span className="ml-auto text-xs text-muted-foreground">
                Best locations
              </span>
            </DropdownMenuItem>
          )}
          
          <DropdownMenuItem
            onClick={() => handleNavigate(rankingsUrl)}
            className="cursor-pointer"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            <span>View Rankings</span>
            <span className="ml-auto text-xs text-muted-foreground">
              All occupations
            </span>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem
            onClick={() => handleNavigate(socGuideUrl)}
            className="cursor-pointer"
          >
            <HelpCircle className="mr-2 h-4 w-4" />
            <span>Learn About SOC Codes</span>
            <ExternalLink className="ml-auto h-3 w-3" />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default SocCodeBadge;
